import { type EditorPlugin } from '../../types/aha-editor';
import {
  Transforms,
  Editor,
  Element,
  Range,
  Point,
  type EditorNextOptions,
  type Descendant,
  Node,
} from 'slate';
import { isKeyHotkey } from 'is-hotkey';
import type AhaEditor from '../../AhaEditor';
import { ListKeydownHandlerPlugin } from '../ListPlugin/ListComponentsPlugin';
import { getInlineElement, isWithinInline } from './utils';
import NodeUtils from '../../AhaEditorUtils/NodeUtils';

// Check if the adjacent node is an inline (not a text node).
const isAdjacentInline = (
  editor: Editor,
  direction: 'next' | 'previous'
): boolean => {
  const options: EditorNextOptions<Descendant> = {
    mode: 'lowest',
    match: (n) => Element.isElement(n) && Editor.isInline(editor, n),
  };
  const { selection } = editor;
  if (!selection) return false;

  const entry =
    direction === 'next'
      ? NodeUtils.nextSibling(editor, selection.anchor.path, options)
      : NodeUtils.previousSibling(editor, selection.anchor.path, options);
  return !!entry;
};

// Check if cursor is currently at the edge of an inline (not a text node).
const isEdgeOfInline = (
  editor: Editor,
  direction: 'next' | 'previous'
): boolean => {
  if (!editor.selection) return false;

  const node = Editor.node(editor, editor.selection.focus);
  if (!node) return false;

  const result = Editor.above(editor, {
    at: editor.selection.focus,
    mode: 'lowest',
    match: (n) => Element.isElement(n) && Editor.isInline(editor, n),
  });
  if (!result) return false;

  const inlineEdges = Editor.edges(editor, result[1]);
  if (direction === 'next') {
    return Point.equals(editor.selection.focus, inlineEdges[1]);
  } else {
    return Point.equals(editor.selection.focus, inlineEdges[0]);
  }
};

const handleEnter = (
  event: KeyboardEvent,
  editor: Editor
): boolean | undefined => {
  const { selection } = editor;
  if (!selection) return;

  const inline = Editor.above(editor, {
    at: selection.focus,
    mode: 'lowest',
    match: (n) => Element.isElement(n) && Editor.isInline(editor, n),
  });

  if (!inline) return;

  const path = inline[1];

  if (Editor.isStart(editor, selection.anchor, path)) {
    const prevPoint = Editor.before(editor, path);
    if (!prevPoint) return;

    event.preventDefault();
    Transforms.splitNodes(editor, {
      always: true,
      at: prevPoint,
    });

    return true;
  }

  if (Editor.isEnd(editor, selection.anchor, path)) {
    const nextPoint = Editor.after(editor, path);
    if (!nextPoint) return;

    event.preventDefault();
    Transforms.select(editor, nextPoint);
    Transforms.splitNodes(editor, {
      always: true,
    });

    return true;
  }
};

// Default left/right behavior is unit:'character'.
// This fails to distinguish between two cursor positions, such as
// <inline>foo<cursor/></inline> vs <inline>foo</inline><cursor/>.
// Here we modify the behavior to unit:'offset'.
// This lets the user step into and out of the inline without stepping over characters.
// You may wish to customize this further to only use unit:'offset' in specific cases.
const handleArrowKeys = (
  event: KeyboardEvent,
  editor: Editor
): boolean | undefined => {
  let direction: 'next' | 'previous' | undefined;
  if (isKeyHotkey('ArrowLeft', event)) {
    direction = 'previous';
  } else if (isKeyHotkey('ArrowRight', event)) {
    direction = 'next';
  } else {
    return;
  }

  const inInline = isWithinInline(editor);
  const edgeOfInline = isEdgeOfInline(editor, direction);
  const adjacentInline = isAdjacentInline(editor, direction);

  if ((inInline && edgeOfInline) || (!inInline && adjacentInline)) {
    event.preventDefault();
    Transforms.move(editor, {
      unit: 'offset',
      reverse: direction === 'previous',
    });
    return true;
  }
};

const handleBackspaceOrDelete = (
  event: KeyboardEvent,
  editor: Editor
): boolean | undefined => {
  const inlineNode = getInlineElement(editor);

  if (!inlineNode) {
    return;
  }

  const edgeOfInline = isEdgeOfInline(
    editor,
    event.key === 'Backspace' ? 'previous' : 'next'
  );

  if (edgeOfInline && Node.string(inlineNode[0]) === '') {
    Transforms.removeNodes(editor, { at: inlineNode[1] });

    // We need to prevent the browser's default handling of the event, otherwise we delete the inline node above, AND the prev/next character.
    event.preventDefault();
    return true;
  }
};

const handleInlineKeyDown = (
  event: KeyboardEvent,
  ahaEditor: AhaEditor
): boolean | undefined => {
  const { editor } = ahaEditor;
  const { selection } = editor;

  if (!selection || !Range.isCollapsed(selection)) return;

  if (isKeyHotkey('ArrowLeft', event) || isKeyHotkey('ArrowRight', event)) {
    return handleArrowKeys(event, editor);
  }

  if (isKeyHotkey('Enter', event)) {
    return handleEnter(event, editor);
  }

  if (isKeyHotkey('Backspace', event) || isKeyHotkey('Delete', event)) {
    return handleBackspaceOrDelete(event, editor);
  }
};

// Behaviors to improve cursor movement around inline elements.
const InlinePlugin: EditorPlugin = {
  // TODO: Handle Enter inside an inline in a better way.

  name: 'Inline keydown plugin',
  // We want the list splitting logic to have priority over soft breaks.
  dependencies: [ListKeydownHandlerPlugin],
  handlers: {
    onKeyDown: handleInlineKeyDown,
  },
};

export default InlinePlugin;
